import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { pose } from "./Player";
import { BENCH_W, WINDOWS, windowsStand } from "../../lib/world";
import { day } from "../../lib/daylight";
import { LOOPS } from "../../lib/windowLoops";
import { imageSrc } from "../../lib/api";
import { audio } from "../../lib/audio";
import { regionAt } from "../../lib/world";
import { P27_OFF } from "./ProjectionHall";
import { drawGarland, GARLAND, PETAL_COLS } from "../../lib/garlands";

const garlandVert = `uniform float uTime, uSeed; varying vec2 vUv;
void main(){ vUv = uv; vec3 p = position;
  float k = smoothstep(0.0, 1.0, uv.y);
  p.x += sin(uTime * 0.9 + p.y * 2.2 + uSeed) * 0.014 * (0.4 + k);
  p.z += sin(uTime * 0.7 + p.x * 3.0 + uSeed) * 0.01;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0); }`;
const garlandFrag = `uniform sampler2D uMap; uniform float uLamp; varying vec2 vUv;
void main(){ vec4 c = texture2D(uMap, vUv); if (c.a < 0.35) discard; gl_FragColor = vec4(c.rgb * (0.96 + 0.08 * uLamp), 1.0);
  #include <colorspace_fragment>
}`;
// a petal or leaf drops from the arch now and then: a pointed shape, never a round ball
const petalVert = `attribute float aA; attribute float aR; uniform float uPx; varying float vA; varying float vR;
void main(){ vA = aA; vR = aR; vec4 mv = modelViewMatrix * vec4(position, 1.0); gl_PointSize = uPx / -mv.z; gl_Position = projectionMatrix * mv; }`;
const petalFrag = `uniform vec3 uCol; varying float vA; varying float vR;
void main(){ vec2 c = gl_PointCoord - 0.5; float ca = cos(vR), sa = sin(vR); vec2 r = vec2(ca * c.x - sa * c.y, sa * c.x + ca * c.y);
  float w = 0.22 * (1.0 - smoothstep(0.0, 0.48, abs(r.y)) ) * (0.6 + 0.4 * sign(r.y) * r.y * 2.0);
  float a = step(abs(r.x), w) * step(abs(r.y), 0.46) * vA;
  if (a < 0.02) discard;
  gl_FragColor = vec4(uCol * (0.85 + 0.3 * (0.5 - r.y)), a);
  #include <colorspace_fragment>
}`;
const PETALS = 3;

const CW = 320;
const CH = 620;
const RES = 0.6;
const V3 = new THREE.Vector3();
const PAD = 0.42;
const { w: WW, h: WH, y0: Y0, z: WZ } = WINDOWS;

const vert = `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
const frag = `uniform sampler2D uMap; uniform float uW, uH, uPad, uNear, uLamp, uTime; varying vec2 vUv;
void main(){
  vec2 p = (vUv - 0.5) * vec2(uW + uPad * 2.0, uH + uPad * 2.0);
  float py = p.y + uH * 0.5;
  float r = uW * 0.5;
  float cy = uH - r;
  float d = py < cy ? max(abs(p.x) - r, -py) : length(vec2(p.x, py - cy)) - r;
  vec3 gold = vec3(1.0, 0.78, 0.42);
  float glowK = 0.42 + 0.35 * uLamp + 0.3 * uNear + 0.04 * sin(uTime * 1.3);
  if (d < 0.0) {
    if (d > -0.075) {
      vec3 wood = mix(vec3(0.95, 0.9, 0.8), vec3(1.0, 0.86, 0.6), 0.35 + 0.4 * uLamp);
      gl_FragColor = vec4(wood * (0.9 + 0.1 * smoothstep(-0.075, 0.0, d)), 1.0);
    } else {
      vec3 c = texture2D(uMap, vec2(p.x / uW + 0.5, py / uH)).rgb;
      c = mix(c, vec3(1.0, 0.98, 0.93), 0.08);
      c += vec3(1.0, 0.97, 0.9) * 0.08 * smoothstep(0.3, 0.0, abs(p.x / uW + 0.5 - py / uH * 0.6 - 0.1));
      c += gold * 0.12 * exp((d + 0.075) * 9.0) * glowK;
      float bar = max(step(abs(p.x), 0.02), step(abs(py - cy), 0.02) * step(-0.07, -abs(p.x) + r));
      c = mix(c, vec3(0.96, 0.92, 0.84), bar);
      gl_FragColor = vec4(c, 1.0);
    }
  } else {
    float a = exp(-d * 5.5) * glowK;
    if (a < 0.01) discard;
    gl_FragColor = vec4(mix(gold, vec3(1.0, 0.93, 0.8), exp(-d * 18.0) * 0.5), min(1.0, a));
  }
  #include <colorspace_fragment>
}`;

function labelTex(text) {
  const c = document.createElement("canvas");
  c.width = 640;
  c.height = 160;
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  const draw = () => {
    const g = c.getContext("2d");
    g.clearRect(0, 0, 640, 160);
    g.fillStyle = "rgba(255,251,244,0.95)";
    g.beginPath();
    g.roundRect(10, 10, 620, 140, 50);
    g.fill();
    g.strokeStyle = "rgba(212,160,80,0.7)";
    g.lineWidth = 3;
    g.stroke();
    g.fillStyle = "#4A2D55";
    g.textAlign = "center";
    g.textBaseline = "middle";
    g.font = `italic 500 ${text.length > 20 ? 44 : 50}px "Cormorant Garamond"`;
    g.fillText(text, 320, 60);
    // the window opens its interactive play directly
    g.fillStyle = "#7A3E8E";
    g.font = `500 34px "Cormorant Garamond"`;
    g.fillText("touch to play ✦", 320, 112);
    t.needsUpdate = true;
  };
  draw();
  document.fonts.load('italic 500 54px "Cormorant Garamond"').then(draw).catch(() => {});
  return t;
}

function useWindowArt(list, paintings) {
  return useMemo(
    () =>
      list.map((w) => {
        const painting = paintings.find((p) => p.slug === w.slug);
        const c = document.createElement("canvas");
        c.width = Math.round(CW * RES);
        c.height = Math.round(CH * RES);
        const tex = new THREE.CanvasTexture(c);
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.generateMipmaps = false;
        tex.minFilter = THREE.LinearFilter;
        const img = new Image();
        img.crossOrigin = "anonymous";
        if (painting) img.src = imageSrc(painting.thumb_url || painting.image_url);
        const mat = new THREE.ShaderMaterial({
          vertexShader: vert,
          fragmentShader: frag,
          transparent: true,
          depthWrite: false,
          uniforms: { uMap: { value: tex }, uW: { value: WW }, uH: { value: WH }, uPad: { value: PAD }, uNear: { value: 0 }, uLamp: day.uLamp, uTime: { value: 0 } },
        });
        const gc = document.createElement("canvas");
        gc.width = GARLAND.cw;
        gc.height = GARLAND.ch;
        drawGarland(gc, w.id, WW, WH);
        const gtex = new THREE.CanvasTexture(gc);
        gtex.colorSpace = THREE.SRGBColorSpace;
        const gUni = { uMap: { value: gtex }, uTime: { value: 0 }, uSeed: { value: w.x }, uLamp: day.uLamp };
        const garland = new THREE.ShaderMaterial({ vertexShader: garlandVert, fragmentShader: garlandFrag, uniforms: gUni });
        const pg = new THREE.BufferGeometry();
        pg.setAttribute("position", new THREE.BufferAttribute(new Float32Array(PETALS * 3), 3));
        pg.setAttribute("aA", new THREE.BufferAttribute(new Float32Array(PETALS), 1));
        pg.setAttribute("aR", new THREE.BufferAttribute(new Float32Array(PETALS), 1));
        const petalMat = new THREE.ShaderMaterial({ vertexShader: petalVert, fragmentShader: petalFrag, transparent: true, depthWrite: false, uniforms: { uPx: { value: 70 }, uCol: { value: new THREE.Color(...PETAL_COLS[w.id]) } } });
        const drops = Array.from({ length: PETALS }, (_, i) => ({ t: -2 - i * 3.3 - Math.random() * 2, x: 0, s: Math.random() * 6 }));
        const g2 = c.getContext("2d");
        g2.setTransform(RES, 0, 0, RES, 0, 0);
        return { ...w, painting, c, g: g2, tex, img, mat, garland, gUni, gtex, pg, petalMat, drops, label: new THREE.MeshBasicMaterial({ map: labelTex(painting?.title || w.slug), transparent: true, toneMapped: false, depthWrite: false }), last: -1, drawn: 0, cache: {} };
      }),
    [list, paintings],
  );
}

const benchMat = new THREE.MeshLambertMaterial({ color: "#D2AE84" });
const shadowMat = new THREE.MeshBasicMaterial({ color: "#5A4632", transparent: true, opacity: 0.16, depthWrite: false });

function Bench() {
  return (
    <group position={[BENCH_W.x, 0, BENCH_W.z]} data-testid="windows-bench">
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.006, 0]} material={shadowMat}>
        <planeGeometry args={[2.2, 0.7]} />
      </mesh>
      <mesh position={[0, 0.44, 0]} material={benchMat}>
        <boxGeometry args={[1.9, 0.07, 0.48]} />
      </mesh>
      {[-0.8, 0.8].map((x) => (
        <mesh key={x} position={[x, 0.2, 0]} material={benchMat}>
          <boxGeometry args={[0.07, 0.4, 0.42]} />
        </mesh>
      ))}
    </group>
  );
}

export function LivingWindows({ paintings, active, still, lite, onOpen }) {
  const arts = useWindowArt(WINDOWS.list, paintings);
  const st = useRef({ levels: {}, sent: 0, full: null });
  useEffect(() => () => audio.windowBeds?.({}), []);
  useEffect(() => {
    if (!active) audio.windowBeds?.({});
  }, [active]);
  const { camera } = useThree();
  useFrame((_, dt) => {
    const now = performance.now() / 1000;
    const s = st.current;
    const inHall = active && regionAt(pose.x, pose.z) === "hall";
    const fx = -Math.sin(pose.yaw);
    const fz = -Math.cos(pose.yaw);
    let nearest = null;
    let nd = Infinity;
    const levels = {};
    arts.forEach((a) => {
      const dx = a.x - pose.x;
      const dz = WZ - pose.z;
      const d = Math.hypot(dx, dz);
      a.dist = d;
      a.facing = (dx * fx + dz * fz) / (d || 1);
      levels[a.id] = inHall ? THREE.MathUtils.smoothstep(7.5 - d, 0, 5.5) : 0;
      a.mat.uniforms.uNear.value += (THREE.MathUtils.smoothstep(4.5 - d, 0, 2.5) - a.mat.uniforms.uNear.value) * Math.min(1, dt * 2);
      a.mat.uniforms.uTime.value = still ? 0 : now;
      if (inHall && a.facing > 0.2 && d < nd) {
        nd = d;
        nearest = a.id;
      }
    });
    s.full = nearest;
    arts.forEach((a) => {
      const ready = a.img.complete && a.img.naturalWidth > 0;
      if (still) {
        if (a.drawn < (ready ? 2 : 1)) {
          LOOPS[a.id](a.g, CW, CH, 8, a.img, a.cache);
          a.tex.needsUpdate = true;
          a.drawn = ready ? 2 : 1;
        }
        return;
      }
      const every = P27_OFF ? (a.id === nearest ? 1 / 30 : 0.4) : a.id === nearest ? (lite ? 1 / 16 : 1 / 20) : lite ? 1 / 12 : 1 / 15;
      V3.set(a.x, Y0 + WH / 2, WZ).project(camera);
      const visible = inHall && a.facing > 0.2 && Math.abs(V3.x) < 1.15 && V3.z < 1;
      if ((visible || a.drawn === 0) && now - a.last >= every) {
        a.last = now;
        LOOPS[a.id](a.g, CW, CH, now, a.img, a.cache);
        a.tex.needsUpdate = true;
        a.drawn++;
      }
      a.gUni.uTime.value = now;
      const pos = a.pg.attributes.position;
      const al = a.pg.attributes.aA;
      const rr = a.pg.attributes.aR;
      a.drops.forEach((p, i) => {
        p.t += Math.min(dt, 0.1);
        if (p.t > 6) Object.assign(p, { t: -(3 + Math.random() * 6), x: (Math.random() - 0.5) * (WW + 0.6), s: Math.random() * 6 });
        const k = Math.max(0, p.t) / 6;
        pos.setXYZ(i, p.x + Math.sin(p.t * 1.6 + p.s) * 0.18, Y0 + WH + 0.05 - k * (Y0 + WH - 0.1), 0.12 + k * 0.5);
        al.setX(i, p.t < 0 ? 0 : Math.min(1, p.t * 2) * (1 - THREE.MathUtils.smoothstep(k, 0.8, 1)));
        rr.setX(i, p.t * 1.3 + p.s);
      });
      pos.needsUpdate = al.needsUpdate = rr.needsUpdate = true;
    });
    if (now - s.sent > 0.25 && Object.keys(levels).some((k) => Math.abs((s.levels[k] || 0) - levels[k]) > 0.02)) {
      s.sent = now;
      s.levels = levels;
      audio.windowBeds?.(levels);
    }
    window.__afaeWindows = {
      list: arts.map((a) => {
        V3.set(a.x, Y0 + WH / 2, WZ).project(camera);
        const sx = Math.round(((V3.x + 1) / 2) * innerWidth);
        const sy = Math.round(((1 - V3.y) / 2) * innerHeight);
        return { id: a.id, slug: a.slug, title: a.painting?.title || null, dist: +a.dist.toFixed(2), level: +(levels[a.id] || 0).toFixed(2), draws: a.drawn, sx, sy };
      }),
      fullRate: nearest,
      still: !!still,
      stand: (slug) => {
        const a = arts.find((x) => x.slug === slug);
        if (a) Object.assign(pose, windowsStand(a.x), { pitch: -0.02 });
      },
      open: (slug) => {
        const a = arts.find((x) => x.slug === slug);
        if (!a?.painting) return;
        Object.assign(pose, windowsStand(a.x), { pitch: -0.02 });
        onOpen(a.painting);
      },
    };
  });
  const tap = (a) => ({
    onClick: (e) => {
      e.stopPropagation();
      if (!active || e.delta > 10 || !a.painting) return;
      Object.assign(pose, windowsStand(a.x), { pitch: -0.02 });
      onOpen(a.painting);
    },
    onPointerOver: () => (document.body.style.cursor = "pointer"),
    onPointerOut: () => (document.body.style.cursor = ""),
  });
  return (
    <group data-testid="living-windows">
      {arts.map((a) => (
        <group key={a.id} position={[a.x, 0, WZ]} rotation={[0, Math.PI, 0]} userData={{ livingWindow: a.id }}>
          <mesh position={[0, Y0 + WH / 2, 0.01]} material={a.mat} renderOrder={2} {...tap(a)}>
            <planeGeometry args={[WW + PAD * 2, WH + PAD * 2]} />
          </mesh>
          {!P27_OFF && <mesh position={[0, Y0 - GARLAND.padBottom + (WH + GARLAND.padTop + GARLAND.padBottom) / 2, 0.035]} material={a.garland} renderOrder={3}>
            <planeGeometry args={[WW + GARLAND.padX * 2, WH + GARLAND.padTop + GARLAND.padBottom, 6, 14]} />
          </mesh>}
          {!still && !P27_OFF && <points geometry={a.pg} material={a.petalMat} renderOrder={4} frustumCulled={false} />}
          <mesh position={[0, Y0 - 0.03, 0.08]} material={benchMat}>
            <boxGeometry args={[WW + 0.24, 0.06, 0.16]} />
          </mesh>
          <mesh position={[0, Y0 - 0.34, 0.03]} material={a.label} {...tap(a)}>
            <planeGeometry args={[1.6, 0.4]} />
          </mesh>
        </group>
      ))}
      <Bench />
    </group>
  );
}
